/** 입고 등록/취소를 나누어 전송하고, 처리 중에는 화면을 잠근다. */
const REGISTER_CHUNK_SIZE = 50;
const DELETE_CHUNK_SIZE = 10;
// POST /inbound/delete-items 의 입력 거절 오류 코드
const INPUT_REJECTED = 2;
const NO_RESPONSE_MESSAGE =
  "서버 응답을 받지 못했습니다. 일부가 처리되었을 수 있으니 목록을 확인해 주세요.";

/** 발주 전체의 입고 상태(order_inbound_state) 표시 */
const INBOUND_STATE_LABELS = {
  NONE: "미입고",
  PARTIAL: "부분 입고",
  COMPLETE: "입고 완료",
  CHECK: "수량 확인",
};
const ORDER_CANCEL_MESSAGE =
  "선택한 입고가 속한 발주의 모든 입고를 취소합니다. 선택하지 않은 입고도 함께 취소됩니다. " +
  "발주에 연결되지 않은 수기 매입은 선택한 입고만 취소됩니다. 계속하시겠습니까?";

export const inbound_state = {
  data() {
    return {
      inboundStateHelp:
        "이 입고가 속한 발주 전체의 입고 상태입니다. 입고 자체의 지급 상태가 아닙니다.",
    };
  },
  methods: {
    /** 수기 매입처럼 발주가 없으면 — */
    resolveInboundState(value) {
      return INBOUND_STATE_LABELS[value] || "—";
    },
    formatInboundState(row) {
      return this.resolveInboundState(row.order_inbound_state);
    },
  },
};

export const inbound_process = {
  mixins: [inbound_state],
  data() {
    return {
      processing: false,
      // 취소 확인창을 띄워 둔 동안 true
      confirming: false,
    };
  },
  computed: {
    /** 처리 중이거나 확인 대기 중이면 조회·등록·취소·수정을 막는다. */
    inboundBusy() {
      return this.processing || this.confirming;
    },
  },
  beforeDestroy() {
    // 화면을 떠난 뒤에는 열려 있던 확인창으로 요청하지 않는다.
    this.inboundLeft = true;
  },
  methods: {
    /**
     * ids를 size 단위로 나누어 순서대로 요청한다. 오류가 발생하면 남은 요청을 중단한다.
     * @param {요청 함수(ids) => Promise} request
     * @param {성공 응답 처리 함수(res, ids)} onSuccess
     * @returns 중단된 경우 사유, 모두 처리된 경우 null
     */
    async requestByChunk(ids, size, text, request, onSuccess) {
      let message = null;
      const loading = this.$loading({
        lock: true,
        text: text,
        background: "rgba(0, 0, 0, 0.5)",
      });
      this.processing = true;
      try {
        for (let i = 0; i < ids.length; i += size) {
          loading.setText(`${text} (${i}/${ids.length})`);
          const chunk = ids.slice(i, i + size);
          const res = await request(chunk);
          if (res.error !== this.system.noError) {
            message = this.util.get_response_error(this, this.title, res)
              .message;
            break;
          }
          onSuccess(res, chunk);
        }
      } catch (e) {
        message = NO_RESPONSE_MESSAGE;
      } finally {
        this.processing = false;
        loading.close();
      }
      return message;
    },
    /** 입고 등록 후 결과를 알린다. 목록 갱신은 호출한 쪽에서 한다. */
    async registerInboundByChunk(ids) {
      let created = 0;
      let skipped = 0;
      let failed = 0;
      let failedMessage = null;
      const message = await this.requestByChunk(
        ids,
        REGISTER_CHUNK_SIZE,
        "입고 등록 중입니다.",
        (chunk) => this.api.registerInbound(chunk),
        (res) => {
          created += res.data.length;
          skipped += (res.skipped || []).length;
          const failedItems = res.failed || [];
          failed += failedItems.length;
          if (!failedMessage && failedItems.length > 0) {
            failedMessage = failedItems[0].message;
          }
        },
      );
      let result = `등록 ${created}건, 건너뜀 ${skipped}건, 실패 ${failed}건`;
      const reason = message || failedMessage;
      if (reason) {
        result += ` (${reason})`;
      }
      this.util.notify(this, reason ? "w" : "s", this.title, result);
    },
    /** 발주 단위 입고 취소 후 결과를 알린다. 목록 갱신은 호출한 쪽에서 한다. */
    async deleteInboundByChunk(ids) {
      const message = await this.requestByChunk(
        ids,
        DELETE_CHUNK_SIZE,
        "입고 취소 중입니다.",
        (chunk) => this.api.deleteInbound(chunk),
        () => {},
      );
      if (message) {
        this.util.notify_error(this, this.title, message);
      }
    },
    /**
     * 입고 단위 입고 취소 후 결과를 알린다. 목록 갱신은 호출한 쪽에서 한다.
     * 건별 실패는 다음 묶음을 계속 처리하고, 요청 자체의 오류·무응답이면 남은 묶음을 보내지 않는다.
     */
    async deleteInboundItemsByChunk(ids, labels = {}) {
      // 화면에는 입고 ID 대신 입고번호를 보여준다.
      const label = (id) => labels[id] || id;
      let cancelled = 0;
      let skipped = 0;
      let responded = 0;
      const failedItems = [];
      let lastResponse = null;
      const message = await this.requestByChunk(
        ids,
        DELETE_CHUNK_SIZE,
        "입고 단위 입고 취소 중입니다.",
        async (chunk) => {
          lastResponse = null;
          lastResponse = await this.api.deleteInboundItems(chunk);
          return lastResponse;
        },
        (res, chunk) => {
          responded += chunk.length;
          cancelled += (res.data || []).length;
          skipped += (res.skipped || []).length;
          failedItems.push(...(res.failed || []));
        },
      );
      let result = `취소 ${cancelled}건, 건너뜀 ${skipped}건, 실패 ${failedItems.length}건`;
      if (failedItems.length > 0) {
        result +=
          " / 실패: " +
          failedItems.map((f) => `${label(f.id)}(${f.message})`).join(", ");
      }
      if (message) {
        // 중단된 묶음과, 그 뒤에 보내지 않은 묶음을 구분한다.
        const rest = ids.slice(responded);
        const stopped = rest.slice(0, DELETE_CHUNK_SIZE);
        const unsent = rest.slice(DELETE_CHUNK_SIZE);
        // 서버가 사유를 보낸 경우 그 문구를 쓴다.
        const reason =
          lastResponse && lastResponse.message ? lastResponse.message : message;
        // 입력 거절(error 2)은 서버가 아무것도 처리하지 않고 돌려보낸 것이다.
        // 그 밖의 오류와 무응답은 일부가 처리되었을 수 있어 결과를 알 수 없다.
        const stoppedLabel =
          lastResponse && lastResponse.error === INPUT_REJECTED
            ? "요청 거절(처리 안 됨)"
            : "처리 결과 미확인";
        result += ` / ${stoppedLabel} ${stopped.length}건(${stopped
          .map(label)
          .join(", ")}), 전송 안 함 ${unsent.length}건 — ${reason}`;
      }
      const type = message ? "e" : failedItems.length > 0 ? "w" : "s";
      this.util.notify(this, type, this.title, result);
    },
    /** 입고 단위 입고 취소 확인 문구. 선택한 입고번호를 스크롤 목록으로 모두 보여준다. */
    inboundItemCancelMessage(ids, labels = {}) {
      const h = this.$createElement;
      return h("div", null, [
        h("div", null, "선택한 입고번호"),
        h(
          "div",
          {
            style: {
              maxHeight: "120px",
              overflowY: "auto",
              margin: "6px 0",
              // 입고 단위 입고 취소 버튼(danger)과 같은 색
              color: "#f56c6c",
              wordBreak: "break-all",
            },
          },
          ids.map((id) => labels[id] || id).join(", "),
        ),
        h(
          "div",
          null,
          `의 ${ids.length}건만 취소합니다. 같은 발주의 다른 입고는 남고, 취소한 수량은 다시 입고할 수 있습니다. ` +
            "해당 재고·미지급·시리얼도 함께 취소됩니다. 계속하시겠습니까?",
        ),
      ]);
    },
    /**
     * 선택한 입고를 확인받은 뒤 취소한다.
     * 확인창을 열기 전에 ID를 복사하므로 확인에 표시한 대상과 요청 대상이 같다.
     * 요청을 보냈다면 성공·실패·무응답·화면 처리 예외와 관계없이 마지막에 reload를 한 번 실행한다.
     * 확인하지 않았거나 그 사이 화면이 바뀌었으면 요청도 reload도 하지 않는다.
     * @param {선택한 행} rows
     * @param {"item": 입고 단위, "order": 발주 단위} unit
     * @param {isValid(): 확인 뒤에도 화면이 그대로인지, reload(): 목록 갱신과 선택 초기화} handlers
     */
    async cancelInboundWithConfirm(rows, unit, handlers) {
      if (this.inboundBusy) {
        return;
      }
      const ids = [...new Set(rows.map((row) => row.id))].sort((a, b) => a - b);
      if (ids.length < 1) {
        this.util.notify_warning(
          this,
          this.title,
          this.$t("message.no_selection"),
        );
        return;
      }
      // 입고번호가 같은 행(품목이 여러 개인 수기 매입)은 순번을 붙여 구분한다.
      const codeCount = {};
      rows.forEach((row) => {
        codeCount[row.code] = (codeCount[row.code] || 0) + 1;
      });
      const labels = {};
      rows.forEach((row) => {
        if (row.code) {
          labels[row.id] =
            codeCount[row.code] > 1 ? `${row.code}-${row.seq}` : row.code;
        }
      });
      const { isValid, reload } = handlers;
      let confirmed = false;
      this.confirming = true;
      try {
        await this.$confirm(
          unit === "item"
            ? this.inboundItemCancelMessage(ids, labels)
            : ORDER_CANCEL_MESSAGE,
          this.title,
          {
            type: "warning",
            confirmButtonText: "네",
            cancelButtonText: "아니오",
          },
        );
        confirmed = true;
      } catch (e) {
        // 사용자가 취소했거나 확인창을 띄우지 못한 경우 요청하지 않는다.
        confirmed = false;
      } finally {
        this.confirming = false;
      }
      if (!confirmed || this.inboundLeft || this.processing) {
        return;
      }
      if (isValid && !isValid()) {
        this.util.notify_warning(
          this,
          this.title,
          "화면이 변경되어 취소하지 않았습니다. 다시 선택해 주세요.",
        );
        return;
      }
      try {
        if (unit === "item") {
          await this.deleteInboundItemsByChunk(ids, labels);
        } else {
          await this.deleteInboundByChunk(ids);
        }
      } finally {
        // 잠금 화면을 만들지 못해 요청하지 않은 경우에도 처리 중 표시를 풀고 목록을 다시 읽는다.
        this.processing = false;
        reload();
      }
    },
  },
};
